import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericField from './GenericField';
import { resolveWidget } from '../../pages/picture-ai/registry';

/**
 * Render an agent's own parameters from the descriptor it ships.
 *
 * Simple fields come from a closed list the generic renderer handles; richer sections name a
 * widget. Neither path is allowed to break the screen: an unknown widget degrades to a raw JSON
 * editor rather than rendering nothing.
 */
const readPath = (source, path) => `${path || ''}`.split('.').filter(Boolean).reduce((value, key) => value?.[key], source);

/** A section is unused while the list its descriptor names under `unusedWhen` holds something. */
const isUnused = (section, params) => {
  const value = section.unusedWhen ? readPath(params, section.unusedWhen) : null;
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

const AgentParamsForm = ({ agent, params, onChange, disabled, widgetContext = {} }) => {
  const { t } = useTranslation();
  const sections = agent?.ui_descriptor?.sections || [];
  const setField = (name, value) => onChange({ ...params, [name]: value });

  if (sections.length === 0) {
    return (
      <textarea
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        rows={12} disabled={disabled}
        value={JSON.stringify(params, null, 2)}
        onChange={(event) => { try { onChange(JSON.parse(event.target.value)); } catch (error) { /* keep typing */ } }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => {
        const Widget = section.widget ? resolveWidget(section.widget) : null;
        const unused = isUnused(section, params);
        return (
          <section key={section.id} className={`space-y-3 ${unused ? 'opacity-60' : ''}`}>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{t(section.title)}</h3>
            {unused && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('Not used while combinations are defined: each combination carries its own setup. Kept for when they are removed.')}
              </p>
            )}

            {section.widget && !Widget && (
              <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                {t('This agent needs a more recent version of the interface; edit the raw parameters below.')}
              </p>
            )}

            {Widget && (
              <Widget
                value={params[section.widgetField || 'question_overrides']}
                onChange={value => setField(section.widgetField || 'question_overrides', value)}
                disabled={disabled}
                widgetProps={section.widgetProps}
                {...widgetContext}
              />
            )}

            {(section.fields || []).map((field) => {
              const FieldWidget = resolveWidget(field.kind);
              if (FieldWidget) {
                return (
                  <div key={field.name}>
                    <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                      {t(field.label)}{field.required && <span className="ml-1 text-red-500">*</span>}
                    </span>
                    <FieldWidget
                      value={params[field.name]}
                      onChange={value => setField(field.name, value)}
                      disabled={disabled}
                      widgetProps={field}
                      {...widgetContext}
                    />
                  </div>
                );
              }
              return (
                <GenericField
                  key={field.name} field={field} disabled={disabled}
                  value={params[field.name]} onChange={value => setField(field.name, value)}
                />
              );
            })}
          </section>
        );
      })}
    </div>
  );
};

export default AgentParamsForm;
